import React, { useMemo, useState } from 'react';
import CustomModal from '../../../../../../shared/custom-modal/custom-modal';
import { Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../../../store';
import AddUrlFields, { createNewValuesFromMap, initialDestTypeToValuesMap } from '../add-url-fields';
import { MAX_DEST_VALUES } from '../../../../../../../utils/constants';
import { hasDuplicatesValueOnArray } from '../../../../../../../utils/util';
import DestTypeGroup from '../dest-type-group';

interface AddUrlProps {
    apnName: string;
    listType: 'black' | 'white' | null;
}

const AddUrl: React.FunctionComponent<AddUrlProps> = observer(({ apnName, listType }) => {
    const { useCaseStore } = useStores();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [destTypeToValuesMap, setDestTypeToValuesMap] = useState(initialDestTypeToValuesMap);
    const [hasErrors, setHasErrors] = useState(false);
    const [activeType, setActiveType] = useState<'black' | 'white'>(listType || 'white');

    const totalItemsInDestTypeToValues = useMemo(() => {
        return createNewValuesFromMap(destTypeToValuesMap)
    }, [destTypeToValuesMap]);

    const combinedList = useMemo(() => {
        const listValues = useCaseStore.destValueByType(activeType, apnName);
        return listValues.concat(totalItemsInDestTypeToValues);
    }, [totalItemsInDestTypeToValues, activeType]);

    const hasDuplicates = useMemo(() => {
        return hasDuplicatesValueOnArray(combinedList);
    }, [combinedList]);

    const openAddUrlModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setDestTypeToValuesMap(initialDestTypeToValuesMap);
        setIsModalOpen(false);
    };

    const maxEntriesExceeded = combinedList.length > MAX_DEST_VALUES;

    const handleAddUrl = () => {
        if (hasErrors || maxEntriesExceeded) return;

        useCaseStore.changeParamValueByApn(apnName, `${activeType}_list_destinations`, combinedList);
        closeModal();
    };

    return (
        <>
            <Button className={"add-url-btn secondary-btn"} type="primary" onClick={openAddUrlModal}>+ URL/IP</Button>
            <CustomModal
                isOpen={isModalOpen}
                width="600px"
                title="Add Destination"
                okText="Add"
                onOk={handleAddUrl}
                okButtonProps={{ disabled: hasErrors || maxEntriesExceeded || !totalItemsInDestTypeToValues.length || hasDuplicates}}
                cancelButtonProps={{ className: 'secondary-btn' }}
                onCancel={closeModal}>
                { !listType && <DestTypeGroup destinationType={activeType} setDestinationType={setActiveType} /> }
                <AddUrlFields
                    isDuplicate={hasDuplicates}
                    isMaxEntries={maxEntriesExceeded}
                    onValuesChange={(newDestTypeToValuesMap) => {
                        setDestTypeToValuesMap((prevState) => ({ ...prevState, ...newDestTypeToValuesMap }));
                    }}
                    setHasErrors={setHasErrors}
                />
            </CustomModal>
        </>
    );
});

export default AddUrl;